import * as React from 'react';
import { IconClose16, Input, IconSearch16, TDeviceType } from '@cian/ui-kit';
import { debounce } from 'throttle-debounce';
import { SearchModalInputIconLayout as IconLayout } from '../../../../components/SearchModal/parts/SearchModalInputIconLayout';
import { DEFAULT_INPUT_BLUR_DELAY, DEFAULT_INPUT_DELAY } from '../../../../constants/delays';
import { INPUT_PLACEHOLDER } from '../../../../constants/strings';

export interface ISearchModalSuggestInputContainerProps {
  deviceType: TDeviceType;
  disabled?: boolean;
  onChange: (value: string) => void;
  onFocus: () => void;
  onBlur?: () => void;
  onClear?: () => void;
}

export interface ISearchModalSuggestInputContainerPublic {
  clearValue(): void;
}

export const SearchModalSuggestInputContainer = React.forwardRef(function SearchModalSuggestInputContainer(
  props: ISearchModalSuggestInputContainerProps,
  ref: React.ForwardedRef<ISearchModalSuggestInputContainerPublic>,
) {
  const { deviceType, disabled, onChange, onFocus, onBlur, onClear } = props;
  const [value, setValue] = React.useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChangeDebounced = React.useCallback(debounce(DEFAULT_INPUT_DELAY, false, onChange), [onChange]);

  const handleChange = React.useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, v: string) => {
      setValue(v);
      handleChangeDebounced(v);
    },
    [handleChangeDebounced],
  );

  const clearValue = React.useCallback(() => {
    setValue('');
    handleChangeDebounced('');
  }, [handleChangeDebounced]);

  const handleClear = React.useCallback(() => {
    clearValue();
    if (onClear) {
      onClear();
    }
  }, [clearValue, onClear]);

  const handleFocus = React.useCallback(() => {
    onFocus();
  }, [onFocus]);

  const handleBlur = React.useCallback(() => {
    setTimeout(() => {
      if (onBlur) {
        onBlur();
      }
    }, DEFAULT_INPUT_BLUR_DELAY);
  }, [onBlur]);

  React.useImperativeHandle(
    ref,
    (): ISearchModalSuggestInputContainerPublic => ({
      clearValue,
    }),
  );

  const inputSize = deviceType === 'phone' ? 'M' : 'XS';

  return (
    <Input
      placeholder={INPUT_PLACEHOLDER}
      leftAdornment={
        <IconLayout>
          <IconSearch16 />
        </IconLayout>
      }
      rightAdornment={
        value && (
          <IconLayout onClick={handleClear}>
            <IconClose16 />
          </IconLayout>
        )
      }
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      size={inputSize}
      value={value}
    />
  );
});
